import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import clsx from "clsx";

import { DataGridPro, GridActionsCellItem } from "@mui/x-data-grid-pro";
import { styled } from "@mui/material/styles";
import { tooltipClasses } from "@mui/material/Tooltip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";

import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";

import { UiLink } from "components/common";
import CustomToolbar from "components/GridToolbar";
import AssociateTransactionModal from "components/Modal/AssociateTransactionModal";
import SolsDataGrid from "components/SolsticeComponents/Sols-data-grid";

import { Container, TableContainer } from "./styles";
import { round } from "components/CommonFns";

import TransactionNewStatusIcon from "components/icons/TransactionNewStatusIcon";
import TransactionOnTrackStatusIcon from "components/icons/TransactionOnTrackStatusIcon";
import TransactionDelayedStatusIcon from "components/icons/TransactionDelayedStatusIcon";
import TransactionCancelledStatusIcon from "components/icons/TransactionCancelledStatusIcon";
import TransactionCompletedStatusIcon from "components/icons/TransactionCompletedStatusIcon";
import PreTransactionNewStatusIcon from "components/icons/PreTransactionNewStatusIcon";
import PreTransactionOnTrackStatusIcon from "components/icons/PreTransactionOnTrackStatusIcon";
import PreTransactionDelayedStatusIcon from "components/icons/PreTransactionDelayedStatusIcon";
import PreTransactionCancelledStatusIcon from "components/icons/PreTransactionCancelledStatusIcon";
import PreTransactionCompletedStatusIcon from "components/icons/PreTransactionCompletedStatusIcon";

import CommentIcon from "@mui/icons-material/Comment";
import CommentsModal from "../CommentsModal";
import CustomTooltip from "components/CustomTooltip/CustomTooltip";

import moment from "moment";
import { formatNumberWithCommas, sanitizeHtml } from "shared/utils";

function CustomNoRowsOverlay() {
  return (
    <Stack
      height="100%"
      alignItems="center"
      justifyContent="center"
      spacing={2}
      sx={{
        minHeight: "280px",
        backgroundColor: "white",
        color: "#01151D",
        fontFamily: "Source Sans Pro",
        fontWeight: 400,
        textAlign: "center",
      }}
    >
      <Typography variant="h6" sx={{ fontSize: "20px", fontFamily: "Source Sans Pro", fontWeight: 400 }}>
        No results were found
      </Typography>
      <Typography variant="body2" sx={{ fontSize: "16px", fontFamily: "Source Sans Pro", fontWeight: 400 }}>
        Try adjusting your search or filters.
      </Typography>
    </Stack>
  );
}

function CustomLoadingOverlay() {
  return (
    <Stack
      height="100%"
      alignItems="center"
      justifyContent="center"
      spacing={2}
      sx={{ minHeight: "280px", backgroundColor: "white" }}
    >
      <LinearProgress sx={{ width: "200px" }} />
      <Typography variant="body2" sx={{ fontSize: "16px", fontFamily: "Source Sans Pro", fontWeight: 400, color: "#01151D" }}>
        Loading data...
      </Typography>
    </Stack>
  );
}

const isEffectivelyEmptyComment = (html) => {
  if (!html || typeof html !== "string") return true;
  const el = document.createElement("div");
  el.innerHTML = sanitizeHtml(html);
  return (el.textContent || el.innerText || "").trim().length === 0;
};

/** Match Transaction grid: CustomTooltip + rich HTML on dark tooltip surface */
const LastCommentTooltip = styled(CustomTooltip)(() => ({
  [`& .${tooltipClasses.tooltip} .html-comment`]: {
    color: "#FFFFFF",
    fontSize: "14px",
    lineHeight: "20px",
    fontFamily: "'Source Sans Pro', sans-serif",
    fontWeight: 400,
    letterSpacing: "0px",
    "& p": {
      margin: "0 0 8px 0",
      padding: 0,
      color: "#FFFFFF",
      "&:last-child": { marginBottom: 0 },
    },
    "& ul, & ol": {
      margin: "0 0 8px 0",
      paddingLeft: "1.25rem",
    },
    "& li": {
      margin: "2px 0",
      color: "#FFFFFF",
      fontSize: "14px",
      lineHeight: "20px",
    },
    "& strong, & b": { fontWeight: 600, color: "#FFFFFF" },
    "& em, & i": { fontStyle: "italic", color: "#FFFFFF" },
    "& u": { textDecoration: "underline" },
    "& s, & strike, & del": {
      textDecoration: "line-through",
      color: "#FFFFFF",
    },
    "& a": { color: "#B8E6FF", textDecoration: "underline" },
    "& .mention": {
      color: "#B8E6FF",
      fontWeight: 500,
      textDecoration: "none",
    },
  },
}));


const TX_STATUS_ICONS = {
  transaction: {
    1: TransactionNewStatusIcon,
    2: TransactionOnTrackStatusIcon,
    3: TransactionDelayedStatusIcon,
    4: TransactionCancelledStatusIcon,
    5: TransactionCompletedStatusIcon,
  },
  pretransaction: {
    1: PreTransactionNewStatusIcon,
    2: PreTransactionOnTrackStatusIcon,
    3: PreTransactionDelayedStatusIcon,
    4: PreTransactionCancelledStatusIcon,
    5: PreTransactionCompletedStatusIcon,
  },
};

const getTxStatusIcon = (transactionCategory, statusId) => {
  const isPreTx = transactionCategory?.toLowerCase() === "pre-transaction";
  const map = isPreTx ? TX_STATUS_ICONS.pretransaction : TX_STATUS_ICONS.transaction;
  return map[statusId] ?? (isPreTx ? PreTransactionNewStatusIcon : TransactionNewStatusIcon);
};


/** Shared with Last Comment: same placement, delay, z-index, max width as Transaction grid */
const leaseEventGridTooltipCommonProps = {
  arrow: true,
  placement: "top",
  enterDelay: 350,
  maxWidth: "520px",
  slotProps: {
    popper: {
      sx: { zIndex: 2000 },
    },
  },
};

const LAST_COMMENT_PREVIEW_MAX_PX = 44;

const LastCommentGridCell = ({ html }) => {
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const [showEllipsis, setShowEllipsis] = useState(false);
  const safeHtml = useMemo(() => sanitizeHtml(html), [html]);

  useLayoutEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;
    const measure = () => {
      const truncated = inner.scrollHeight > outer.clientHeight + 1;
      setShowEllipsis((prev) => (prev === truncated ? prev : truncated));
    };
    measure();
    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(outer);
    return () => ro?.disconnect();
  }, [html]);

  const tooltipBody = (
    <div
      className="html-comment"
      dangerouslySetInnerHTML={{ __html: safeHtml }}
    />
  );

  return (
    <LastCommentTooltip title={tooltipBody} {...leaseEventGridTooltipCommonProps}>
      <div
        ref={outerRef}
        className={clsx(
          "last-comment-preview-wrap",
          showEllipsis && "last-comment-preview-wrap--truncated"
        )}
        style={{ maxHeight: LAST_COMMENT_PREVIEW_MAX_PX }}
      >
        <div
          ref={innerRef}
          className="html-comment last-comment-preview-html"
          dangerouslySetInnerHTML={{ __html: safeHtml }}
        />
      </div>
    </LastCommentTooltip>
  );
};

function getRentableArea(params) {
  var input = `${
    params.row.rentableBuildingArea
      ? round(params.row.rentableBuildingArea, 2)
      : 0
  }`;
  return renderCellValue(`${formatNumberWithCommas(input)} ${params.row.uomNameShort}`);
}

function formatDate(params) {
  if (!params.value) return "";
  const formattedDate = moment.utc(params.value).format("DD MMM YYYY");
  return renderCellValue(formattedDate);
}

// Wraps a plain cell value in the app's styled CustomTooltip so hovering a
// truncated cell reveals its full text (matches the Transaction grid).
const renderCellValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  return (
    <CustomTooltip title={String(value)} arrow placement="top" enterDelay={500}>
      <span
        style={{
          display: "block",
          width: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </span>
    </CustomTooltip>
  );
};

const LeaseEventsTable = (props) => {
  const {
    leaseEvents,
    eventType,
    totalLeaseEvents,
    loading,
    hideColumns,
    hideToolbar,
    showCheckbox = true,
    onLeaseNameClicked,
    onPageChange,
    onSortModelChange,
    onRefresh = () => {},
    selectionModel,
    onRowSelectionModelChange,
    actionIcons,
    onDelete,
    columnVisibilityModel,
    columnVisibilityModelChange,
    columnOrderSettings,
    columnOrderSettingsChange,
    onGridColumnsChange = () => {},
    isAdmin,
    pageSize,
    parent,
  } = props;

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  useEffect(()=>{
      setPaginationModel({page:0, pageSize:pageSize});
    },[pageSize]);

  const [selectedComment, setSelectedComment] = useState({});
  const [isCommentsModalOpen, setIsCommentsModalOpen] = useState(false);
  const [assocTxModalOpen, setAssocTxModalOpen] = useState(false);
  const [selectedAssocTxData, setSelectedAssocTxData] = useState({ propertyName: "", transactions: [] });

  // State for grid columns
  const [gridColumns, setGridColumns] = useState([]);

  // Function to sort columns based on order settings
  const sortColumnsByOrder = (columns, orderSettings) => {
    if (!orderSettings || orderSettings.length === 0) {
      return [...columns];
    }

    // Define pinned columns for lease events
    const pinnedLeft = ["eventId", "leaseName"];
    const pinnedRight = ["actions"];

    // Create a map of field names to order values
    const orderMap = {};
    orderSettings.forEach(setting => {
      orderMap[setting.column] = setting.order !== undefined ? setting.order : 999;
    });

    // Separate columns into pinned and non-pinned
    const leftPinnedColumns = columns.filter(col => pinnedLeft.includes(col.field));
    const rightPinnedColumns = columns.filter(col => pinnedRight.includes(col.field));
    const nonPinnedColumns = columns.filter(col => 
      !pinnedLeft.includes(col.field) && !pinnedRight.includes(col.field)
    );

    // Sort non-pinned columns by their order value
    const sortedNonPinned = nonPinnedColumns.sort((a, b) => {
      const orderA = orderMap[a.field] !== undefined ? orderMap[a.field] : 999;
      const orderB = orderMap[b.field] !== undefined ? orderMap[b.field] : 999;
      return orderA - orderB;
    });

    // Combine: left pinned + sorted non-pinned + right pinned
    return [...leftPinnedColumns, ...sortedNonPinned, ...rightPinnedColumns];
  };

  const renderLeaseName = (params) => {
    return (
      <CustomTooltip title={params.row.leaseName || ""} arrow placement="top" enterDelay={500}>
        <span
          style={{
            display: "inline-block",
            maxWidth: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            verticalAlign: "middle",
            color: '#09779E',
            textDecoration: 'underline',
            textDecorationColor: '#09779E',
            textUnderlineOffset: '2px',
            cursor: 'pointer',
            fontFamily: '"Source Sans Pro"',
            fontSize: '16px',
            fontWeight: 400,
          }}
          onClick={() => {
            onLeaseNameClicked(params.row.leaseEventId);
          }}
        >
          {params.row.leaseName}
        </span>
      </CustomTooltip>
    );
  };

  const handleOpenAssocTxModal = (propertyName, transactions) => {
    setSelectedAssocTxData({ propertyName, transactions: transactions || [] });
    setAssocTxModalOpen(true);
  };

  const handleCloseAssocTxModal = () => {
    setAssocTxModalOpen(false);
  };

  const renderAssociatedTransactions = (params) => {
    const { transactions, propertyName, leaseName } = params.row;
    const displayName = propertyName || leaseName || "";

    if (!transactions || transactions.length === 0) {
      return (
        <span
          style={{
            fontFamily: '"Source Sans Pro"',
            fontSize: "16px",
            fontWeight: 400,
            color: "#56656B",
          }}
        >
          NA
        </span>
      );
    }

    const firstTx = transactions[0];
    const FirstStatusIcon = getTxStatusIcon(firstTx.transactionCategory, firstTx.transactionStatusId);
    const extraCount = transactions.length - 1;

    const cellContent = (
      <>
        <FirstStatusIcon fontSize="small" style={{ flexShrink: 0 }} />
        <span
          style={{
            fontFamily: '"Source Sans Pro"',
            fontSize: "14px",
            fontWeight: 400,
            color: "#09779E",
            textDecoration: "underline",
            textDecorationColor: "#DFE5E8",
            textUnderlineOffset: "2px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {firstTx.transactionName || ""}
        </span>
        {extraCount > 0 && (
          <span
            style={{
              fontFamily: '"Source Sans Pro"',
              fontSize: "13px",
              fontWeight: 400,
              color: "#56656B",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            +{extraCount}
          </span>
        )}
      </>
    );

    return (
      <CustomTooltip title={firstTx.transactionName || ""} arrow placement="top" enterDelay={500}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            overflow: "hidden",
            width: "100%",
            lineHeight: "normal",
            cursor: "pointer",
          }}
          onClick={() => handleOpenAssocTxModal(displayName, transactions)}
        >
          {cellContent}
        </div>
      </CustomTooltip>
    );
  };

  const onEditComment = (data) => {
    setIsCommentsModalOpen(true);
    setSelectedComment(data);
  };

  const handleCommentSave = () => {
    // onRefresh();
  };

  const renderDate = (params) => {
    if (!params.value) return "";
    return moment.utc(params.value).local().format("DD MMM YYYY");
  };

  const renderEventNote = (params) => {
    const raw = params.value;
    const text = raw != null && String(raw).trim() !== "" ? String(raw) : "";
    if (!text) {
      return "-";
    }
    return (
      <CustomTooltip title={text} {...leaseEventGridTooltipCommonProps}>
        <div className="event-note-cell">{text}</div>
      </CustomTooltip>
    );
  };

  const renderLastComment = (params) => {
    const html = params.value ?? params.row?.lastComment;
    if (!html || isEffectivelyEmptyComment(html)) {
      return "-";
    }
    return <LastCommentGridCell html={html} />;
  };

  /* eventId and leaseName are fixed as left-pinned columns (positions 1 and 2); order not changeable in column visibility modal */
  let defaultColumns = [
    {
      field: "eventId",
      headerName: "Event Id",
      headerClassName: "grid-app-header",
      minWidth: 100,
      width: 120,
      maxWidth: 120,
      flex: 0,
      hideable: false,
      renderCell: (params) => renderCellValue(params.value),
    },
    {
      field: "leaseName",
      headerName: "Lease Name",
      renderCell: renderLeaseName,
      headerClassName: "grid-app-header",
      minWidth: 200,
      width: 250,
      maxWidth: 250,
      flex: 0,
      hideable: false,
    },
    {
      field: "leaseEventId",
      headerName: "TME Event Id",
      headerClassName: "grid-app-header",
      minWidth: 100,
      flex: 1,
      hideable: isAdmin,
      renderCell: (params) => renderCellValue(params.value),
    },
    {
      field: "leaseCode",
      headerName: "Tenure Id",
      headerClassName: "grid-app-header",
      minWidth: 100,
      flex: 1,
      renderCell: (params) => renderCellValue(params.value),
    },
    {
      field: "propertyName",
      headerName: "Property Name",
      headerClassName: "grid-app-header",
      minWidth: 200,
      // hideable: false,
      flex: 1,
      renderCell: (params) => renderCellValue(params.value),
    },
    {
      field: "streetAddress1",
      headerName: "Property Address",
      headerClassName: "grid-app-header",
      minWidth: 200,
      flex: 1,
      renderCell: (params) => renderCellValue(params.value),
    },
    {
      field: "businessUnit",
      headerName: "Business Unit",
      headerClassName: "grid-app-header",
      minWidth: 200,
      flex: 1,
      renderCell: (params) => renderCellValue(params.value),
    },
    {
      field: "countryName",
      headerName: "Country",
      headerClassName: "grid-app-header",
      minWidth: 150,
      flex: 1,
      renderCell: (params) => renderCellValue(params.value),
    },
    {
      field: "stateProvince",
      headerName: "State / Province",
      headerClassName: "grid-app-header",
      minWidth: 150,
      flex: 1,
      renderCell: (params) => renderCellValue(params.value),
    },
    {
      field: "city",
      headerName: "City",
      headerClassName: "grid-app-header",
      minWidth: 150,
      flex: 1,
      renderCell: (params) => renderCellValue(params.value),
    },
    {
      field: "rentableBuildingArea",
      headerName: "Building Area",
      renderCell: getRentableArea,
      headerClassName: "grid-app-header",
      minWidth: 100,
      flex: 1,
    },
    {
      field: "eventType",
      headerName: "Event Type",
      headerClassName: "grid-app-header",
      minWidth: 150,
      flex: 1,
      renderCell: (params) => renderCellValue(params.value),
    },
    {
      field: "leaseEventStatusName",
      headerName: "Event Status",
      headerClassName: "grid-app-header",
      minWidth: 150,
      flex: 1,
      renderCell: (params) => renderCellValue(params.value),
    },
    {
      field: "eventNote",
      headerName: "Event Note",
      headerClassName: "grid-app-header",
      minWidth: 150,
      flex: 1,
      renderCell: renderEventNote,
    },
    {
      field: "tenureType",
      headerName: "Tenure Type",
      headerClassName: "grid-app-header",
      minWidth: 100,
      hideable: false,
      flex: 1,
      renderCell: (params) => renderCellValue(params.value),
    },
    {
      field: "lastComment",
      headerName: "Last Comment",
      renderCell: renderLastComment,
      headerClassName: "grid-app-header",
      minWidth: 150,
      flex: 1,
    },
    {
      field: "lastCommentCreatedDate",
      headerName: "Last Comment Date",
      renderCell: formatDate,
      headerClassName: "grid-app-header",
      minWidth: 150,
      flex: 1,
    },
    {
      field: "noticeDate",
      headerName: "Notice Date",
      renderCell: formatDate,
      headerClassName: "grid-app-header",
      minWidth: 150,
      flex: 1,
    },
    {
      field: "actionDate",
      headerName: "Event Date",
      renderCell: formatDate,
      headerClassName: "grid-app-header",
      minWidth: 150,
      flex: 1,
    },
    {
      field: "expireDate",
      headerName: "Lease End Date",
      headerClassName: "grid-app-header",
      renderCell: formatDate,
      minWidth: 150,
      flex: 1,
    },
    {
      field: "snoozedUntil",
      headerName: "Snoozed Until",
      renderCell: formatDate,
      headerClassName: "grid-app-header",
      minWidth: 100,
      flex: 1,
    },
    {
      field: "snoozedComment",
      headerName: "Snoozed Comment",
      headerClassName: "grid-app-header",
      minWidth: 150,
      flex: 1,
      renderCell: (params) => renderCellValue(params.value),
    },
    {
      field: "leaseEventIgnoredDate",
      headerName: "Ignored Date",
      renderCell: formatDate,
      headerClassName: "grid-app-header",
      minWidth: 100,
      flex: 1,
    },
    {
      field: "ignoreComment",
      headerName: "Ignore Comment",
      headerClassName: "grid-app-header",
      minWidth: 150,
      flex: 1,
      renderCell: (params) => renderCellValue(params.value),
    },
    {
      field: "transactions",
      headerName: "Associated Transactions",
      headerClassName: "grid-app-header",
      renderCell: renderAssociatedTransactions,
      sortable: false,
      minWidth: 200,
      flex: 1,
    },
    {
      field: "actions",
      type: "actions",
      minWidth: 100,
      width: 150,
      maxWidth: 150,
      flex: 0,
      headerName: "Actions",
      headerClassName: "grid-app-header action-icons",
      getActions: (params) => {
        let actionCellItems = [];

        if (actionIcons?.includes("comment")) {
          actionCellItems.push(
            <GridActionsCellItem
              icon={
                <CustomTooltip title="Comments" {...leaseEventGridTooltipCommonProps}>
                  <CommentIcon />
                </CustomTooltip>
              }
              label="Edit Comments"
              onClick={() => onEditComment(params.row)}
            />
          );
        }

        if (actionIcons?.includes("delete")) {
          actionCellItems.push(
            <GridActionsCellItem
              icon={
                <CustomTooltip title="Delete" {...leaseEventGridTooltipCommonProps}>
                  <DeleteOutlineOutlinedIcon />
                </CustomTooltip>
              }
              label="Delete"
              onClick={() => onDelete(params.id)}
            />
          );
        }

        return actionCellItems;
      },
    },
  ];

  let columns = [];
  switch (eventType) {
    case "all":
    default:
      columns = defaultColumns.filter((column) => {
        return (
          !column.field.toLowerCase().includes("ignore") &&
          !column.field.toLowerCase().includes("snooze")
        );
      });
      break;
    case "snoozed":
      columns = defaultColumns.filter((column) => {
        return !column.field.toLowerCase().includes("ignore");
      });
      break;

    case "ignored":
      columns = defaultColumns.filter((column) => {
        return !column.field.toLowerCase().includes("snooze");
      });
      break;
  }

  if (hideColumns) {
    hideColumns.forEach((hc) => {
      columns = columns.filter((c) => c.field !== hc);
    });
  }

  if (!actionIcons || actionIcons.length === 0) {
    columns = columns.filter((c) => c.field !== "actionIcons");
  }

  // Effect to sort and set grid columns based on order settings
  useEffect(() => {
    let finalColumns = [...columns];
    if (hideColumns) {
      hideColumns.forEach((hc) => {
        finalColumns = finalColumns.filter((c) => c.field !== hc);
      });
    }
    
    // Sort columns based on order settings
    const sortedColumns = sortColumnsByOrder(finalColumns, columnOrderSettings);
    setGridColumns(sortedColumns);
  }, [columnOrderSettings, eventType, hideColumns]);

  useEffect(() => {
    const effectiveColumns = gridColumns.length > 0 ? gridColumns : columns;
    onGridColumnsChange(effectiveColumns);
  }, [gridColumns, columns, onGridColumnsChange]);

  // SolsDataGrid calls onPageChange(page, pageSize) with two args; parent expects { page, pageSize }
  const handlePageChange = (page, pageSize) => {
    setPaginationModel({ page, pageSize });
    onPageChange({ page, pageSize });
  };

  const renderTableComponents = () => {
    if (hideToolbar) return { Toolbar: () => null };
    return { Toolbar: CustomToolbar };
  };

  return (
    <>
      {isCommentsModalOpen && (
        <CommentsModal
          open={isCommentsModalOpen}
          onClose={() => {
            setIsCommentsModalOpen(false);
            onRefresh();
          }}
          onSave={handleCommentSave}
          id={selectedComment.leaseEventId}
          data={leaseEvents}
        />
      )}
      <Container $autoHeight={parent === "snooze"}>
        <TableContainer>
          <SolsDataGrid
            height={parent === "snooze" ? "auto" : "calc(100vh - 190px)"}
            autoHeight={parent === "snooze"}
            loading={loading}
            getRowId={(row) => row.leaseEventId}
            getCellClassName={(params) => {
              if (params.field === "lastComment") {
                return "last-comment-data-cell";
              }
              return "";
            }}
            columns={gridColumns.length > 0 ? gridColumns : columns}
            rows={leaseEvents}
            rowCount={totalLeaseEvents}
            pageSizeOptions={[10, 20, 30, 40, 50, 100]}
            slotProps={{
                pagination: {
                    showFirstButton: true,
                    showLastButton: true,
                },
            }}
            paginationModel={paginationModel}
            pagination
            paginationMode="server"
            onPageChange={handlePageChange}
            sortingMode="server"
            onSortModelChange={onSortModelChange}
            checkboxSelection={showCheckbox} 
            rowSelectionModel={selectionModel}
            onRowSelectionModelChange={onRowSelectionModelChange}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={columnVisibilityModelChange}
            onColumnOrderChange={(settings) => {
              if (columnOrderSettingsChange) {
                columnOrderSettingsChange(settings, gridColumns.length > 0 ? gridColumns : columns);
              }
            }}
            pinnedColumns={{
              left: ["__check__", "eventId", "leaseName"],
              right: ["actions"],
            }}
            showCellVerticalBorder={false}
            disableColumnFilter
            disableDensitySelector
            disableColumnMenu
          />
        </TableContainer>
      </Container>
      <AssociateTransactionModal
        open={assocTxModalOpen}
        onClose={handleCloseAssocTxModal}
        propertyName={selectedAssocTxData.propertyName}
        transactions={selectedAssocTxData.transactions}
        from="lease-event"
      />
    </>
  );
};

export default LeaseEventsTable;
